import React, { useState, useEffect } from 'react';
import {
    Box,
    Drawer,
    Typography,
    IconButton,
    Tabs,
    Tab,
    TextField,
    Button,
    useTheme,
    Stack,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip,
    Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { departmentService, Department } from '../../services/department-service';
import { dataElementsService, DataElement, DataElementType } from '../../services/dataelements-service';
import { collectionPointsService } from '../../services/collectionpoints-service';
import { config } from '../../config/config';

const COLLECTION_POINT_TYPES = [
    { 
        value: 'API', 
        label: 'API',
        description: 'Privigo provides API endpoints for consent management. Clients create and host their own forms, then submit consent records through the API.'
    },
    { 
        value: 'HOSTED_WEBFORM', 
        label: 'Hosted Webform',
        description: 'Use Privigo form designer to create forms hosted on our platform. Forms can be displayed on client websites using iframe integration.'
    },
    { 
        value: 'EMBEDDED_WEBFORM', 
        label: 'Embedded Webform',
        description: 'Forms created with Privigo designer are embedded directly into client websites using JavaScript integration.'
    },
    { 
        value: 'NOTICE', 
        label: 'Notice',
        description: 'For existing data principals with client relationships. Create notices to request additional information or consent, accessible through the DP portal.'
    },
    { 
        value: 'BULK_UPLOAD', 
        label: 'Bulk Upload',
        description: 'Upload existing PII data collected offline (e.g., paper forms) via Excel sheets. Data principals receive email notices to provide consent.'
    }
];

interface AddCollectionPanelProps {
    open: boolean;
    onClose: () => void;
    onSave?: () => void;
}

const AddCollectionPanel: React.FC<AddCollectionPanelProps> = ({ open, onClose, onSave }) => {
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [departmentId, setDepartmentId] = useState<number | ''>('');
    const [collectionPointType, setCollectionPointType] = useState('API');
    const [identifierDataElementId, setIdentifierDataElementId] = useState<number | ''>('');
    const [nameError, setNameError] = useState('');
    const [departmentError, setDepartmentError] = useState('');
    const [identifierError, setIdentifierError] = useState('');

    // Loading and error states
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Form state tracking
    const [hasChanges, setHasChanges] = useState(false);
    const [initialFormState, setInitialFormState] = useState({
        name: '',
        description: '',
        departmentId: '',
        collectionPointType: 'API',
        identifierDataElementId: ''
    });

    const [departments, setDepartments] = useState<Department[]>([]);
    const [dataElements, setDataElements] = useState<DataElement[]>([]);
    const [dataElementTypes, setDataElementTypes] = useState<DataElementType[]>([]);
    const [identifierDataElements, setIdentifierDataElements] = useState<DataElement[]>([]);

    useEffect(() => {
        if (open) {
            // Reset form
            setName('');
            setDescription('');
            setDepartmentId('');
            setCollectionPointType('API');
            setIdentifierDataElementId('');
            setNameError('');
            setDepartmentError('');
            setIdentifierError('');
            setError(null);
            setSuccess(null);
            setHasChanges(false);

            // Set initial state
            setInitialFormState({
                name: '',
                description: '',
                departmentId: '',
                collectionPointType: 'API',
                identifierDataElementId: ''
            });

            // Load data
            loadDepartments();
            loadIdentifierDataElements();
            loadDataElementTypes();
        }
    }, [open]);

    // Track form changes
    useEffect(() => {
        setHasChanges(checkForChanges());
    }, [name, description, departmentId, collectionPointType, identifierDataElementId]);

    const loadDepartments = async () => {
        try {
            const depts = await departmentService.getDepartments();
            setDepartments(depts);
        } catch (error) {
            console.error('Error loading departments:', error);
        }
    };

    const loadIdentifierDataElements = async () => {
        try {
            const response = await dataElementsService.getDataElements({
                isIdentifier: true,
                isPublished: true  // Only show published identifier data elements
            });
            setIdentifierDataElements(response.elements);
        } catch (error) {
            console.error('Error loading identifier data elements:', error);
        }
    };

    const loadDataElementTypes = async () => {
        try {
            const types = await dataElementsService.getDataElementTypes();
            setDataElementTypes(types);
        } catch (error) {
            console.error('Error loading data element types:', error);
        }
    };

    const checkForChanges = () => {
        const currentState = {
            name,
            description,
            departmentId,
            collectionPointType,
            identifierDataElementId
        };
        
        return JSON.stringify(currentState) !== JSON.stringify(initialFormState);
    };

    const handleClose = () => {
        if (hasChanges && !isSaving) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?');
            if (!confirmed) {
                return;
            }
        }
        onClose();
    };

    const validateName = (value: string): boolean => {
        if (!value.trim()) {
            setNameError('Name is required');
            return false;
        }
        if (value.trim().length < 3) {
            setNameError('Name must be at least 3 characters long');
            return false;
        }
        setNameError('');
        return true;
    };

    const validateDepartment = (value: number | ''): boolean => {
        if (!value) {
            setDepartmentError('Department is required');
            return false;
        }
        setDepartmentError('');
        return true;
    };

    const validateIdentifier = (value: number | ''): boolean => {
        if (!value) {
            setIdentifierError('Identifier Data Element is required');
            return false;
        }
        setIdentifierError('');
        return true;
    };

    const validateForm = (): boolean => {
        const isNameValid = validateName(name);
        const isDepartmentValid = validateDepartment(departmentId);
        const isIdentifierValid = validateIdentifier(identifierDataElementId);

        return isNameValid && isDepartmentValid && isIdentifierValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            setError('Please fix the validation errors before saving');
            return;
        }

        try {
            setIsSaving(true);
            setError(null);

            const collectionPointData = {
                DepartmentId: departmentId as number,
                Name: name.trim(),
                Description: description.trim() || undefined,
                CollectionPointType: collectionPointType as 'API' | 'HOSTED_WEBFORM' | 'EMBEDDED_WEBFORM' | 'BULK_UPLOAD',
                IdentifierDataElementId: identifierDataElementId as number,
                TrackGeolocation: false // Default value
            };

            await collectionPointsService.createCollectionPoint(collectionPointData);
            
            setSuccess('Collection point created successfully');
            setHasChanges(false);
            
            // Close panel and refresh grid after a short delay to show success message
            setTimeout(() => {
                onClose();
                onSave?.();
            }, 1500);
            
        } catch (error: any) {
            console.error('Error creating collection point:', error);
            setError(error.response?.data?.message || 'Failed to create collection point');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: '600px',
                        bgcolor: theme.palette.background.paper,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                    },
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}>
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>
                            Add Collection Point
                        </Typography>
                        <IconButton onClick={handleClose} sx={{ color: theme.palette.text.primary }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Tabs */}
                    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <Tabs
                            value={currentTab}
                            onChange={(_, v) => setCurrentTab(v)}
                            sx={{
                                '& .MuiTab-root': {
                                    color: theme.palette.text.secondary,
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                    },
                                },
                            }}
                        >
                            <Tab label="Details" />
                        </Tabs>
                    </Box>

                    {/* Tab Panels */}
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {currentTab === 0 && (
                            <Box sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <TextField 
                                        label="Name" 
                                        fullWidth 
                                        size="small" 
                                        required 
                                        value={name} 
                                        onChange={e => {
                                            setName(e.target.value);
                                            validateName(e.target.value);
                                        }}
                                        error={!!nameError}
                                        helperText={nameError}
                                    />
                                    <TextField 
                                        label="Description" 
                                        fullWidth 
                                        size="small" 
                                        multiline 
                                        rows={3} 
                                        value={description} 
                                        onChange={e => setDescription(e.target.value)} 
                                    />
                                    
                                    <FormControl fullWidth size="small" error={!!departmentError} required>
                                        <InputLabel>Department</InputLabel>
                                        <Select
                                            value={departmentId}
                                            label="Department"
                                            onChange={e => {
                                                setDepartmentId(e.target.value as number);
                                                validateDepartment(e.target.value as number);
                                            }}
                                        >
                                            {departments.map(dept => (
                                                <MenuItem key={dept.departmentId} value={dept.departmentId}>
                                                    {dept.departmentName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {departmentError && (
                                            <Typography color="error" variant="caption" sx={{ mt: 1, ml: 2 }}>
                                                {departmentError}
                                            </Typography>
                                        )}
                                    </FormControl>

                                    <FormControl fullWidth size="small">
                                        <InputLabel>Collection Point Type</InputLabel>
                                        <Select
                                            value={collectionPointType}
                                            label="Collection Point Type"
                                            onChange={e => setCollectionPointType(e.target.value)}
                                        >
                                            {COLLECTION_POINT_TYPES.map(type => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {collectionPointType && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
                                            {COLLECTION_POINT_TYPES.find(type => type.value === collectionPointType)?.description}
                                        </Typography>
                                    )}

                                    <FormControl fullWidth size="small" error={!!identifierError} required>
                                        <InputLabel>Identifier Data Element</InputLabel>
                                        <Select
                                            value={identifierDataElementId}
                                            label="Identifier Data Element"
                                            onChange={e => {
                                                setIdentifierDataElementId(e.target.value as number);
                                                validateIdentifier(e.target.value as number);
                                            }}
                                        >
                                            {identifierDataElements.map(element => (
                                                <MenuItem key={element.elementId} value={element.elementId}>
                                                    {element.elementName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {identifierError && (
                                            <Typography color="error" variant="caption" sx={{ mt: 1, ml: 2 }}>
                                                {identifierError}
                                            </Typography>
                                        )}
                                    </FormControl>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -2, mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            This field defines the unique identifier used to distinguish individual data principals when they interact with this collection point
                                        </Typography>
                                        <Tooltip title="This field defines the unique identifier used to distinguish individual data principals when they interact with this collection point">
                                            <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                        </Tooltip>
                                    </Box>
                                    {identifierDataElementId && (
                                        <Box sx={{ mt: 1, ml: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {identifierDataElements.find(element => element.elementId === identifierDataElementId)?.description || 'No description available'}
                                            </Typography>
                                            {(() => {
                                                const selectedElement = identifierDataElements.find(element => element.elementId === identifierDataElementId);
                                                return selectedElement?.type?.typeName && (
                                                    <Chip
                                                        label={selectedElement.type.typeName}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            alignSelf: 'flex-start',
                                                            backgroundColor: theme.palette.background.paper,
                                                            borderColor: theme.palette.divider,
                                                            color: theme.palette.text.secondary,
                                                            fontSize: '0.75rem',
                                                            height: '20px'
                                                        }}
                                                    />
                                                );
                                            })()}
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                        <Button 
                                            variant="outlined" 
                                            color="primary" 
                                            onClick={handleClose}
                                            disabled={isSaving}
                                        >
                                            Cancel
                                        </Button>
                                        <Button 
                                            variant="contained" 
                                            color="primary"
                                            onClick={handleSave}
                                            disabled={isSaving || !hasChanges}
                                            sx={{ minWidth: 100, position: 'relative' }}
                                        >
                                            {isSaving ? (
                                                <>
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{
                                                            color: theme.palette.primary.contrastText,
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            marginTop: '-10px',
                                                            marginLeft: '-10px',
                                                        }}
                                                    />
                                                    <span style={{ visibility: 'hidden' }}>Save</span>
                                                </>
                                            ) : (
                                                'Save'
                                            )}
                                        </Button>
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Drawer>

            {/* Error Snackbar */}
            <Snackbar 
                open={!!error} 
                autoHideDuration={config.ui.snackbar.autoHideDuration}
                onClose={() => setError(null)}
                anchorOrigin={config.ui.snackbar.anchorOrigin}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            {/* Success Snackbar */}
            <Snackbar 
                open={!!success} 
                autoHideDuration={3000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
                    {success}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddCollectionPanel; 